const SynchronizationState = new Enumeration(["Synchronized", "Modified", "Conflict", "Stale", "Failed"]);

class EntityStore {
  constructor() {
    this.databaseName = "EntityStore";
    this.entityStoreName = "entities";
    this.thumbnailStoreName = "thumbnails";
    this.database = null;

    this.databaseReady = this.openDatabase();
  }

  openDatabase() {
    return new Promise(
      (resolve, reject) => {
        const version = 2;
        const request = indexedDB.open(this.databaseName, version);

        request.onsuccess = (event) => {
          this.database = event.target.result;
          resolve();
        };

        request.onupgradeneeded = (event) => {
          const database = event.target.result;

          if (!database.objectStoreNames.contains(this.entityStoreName))
            database.createObjectStore(this.entityStoreName, { keyPath: "Uri" });

          if (!database.objectStoreNames.contains(this.thumbnailStoreName)) {
            const thumbnailStore = database.createObjectStore(this.thumbnailStoreName, { keyPath: ["Entity", "Id" ] });
            thumbnailStore.createIndex("Entity", "Entity", { unique: false });
            thumbnailStore.createIndex("Id", "Id", { unique: false });
          }
        };

        request.onerror = (event) => reject(event.target.error);
      }
    );
  }

  performTransaction(storeNames, mode, callback) {
    return this.databaseReady.then(
      () => new Promise(
        (resolve, reject) => {
          const transaction = this.database.transaction(storeNames, mode);
          const stores = storeNames.map(name => transaction.objectStore(name));

          callback(...stores, transaction, resolve, reject);
        }
      )
    );
  }

  add(entity) {
    return this.performTransaction(
      [this.entityStoreName],
      "readwrite",
      (store, transaction, resolve, reject) => {
        transaction.oncomplete = () => resolve(entity);
        transaction.onerror = (event) => reject(event.target.error);
        store.put(entity);
      }
    );
  }

  delete(entity) {
    return this.performTransaction(
      [this.entityStoreName, this.thumbnailStoreName],
      "readwrite",
      (entityStore, thumbnailStore, transaction, resolve, reject) => {
        const index = thumbnailStore.index("Entity");
        const range = IDBKeyRange.only(entity);
        const request = index.openCursor(range);

        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          }
        };

        entityStore.delete(entity);

        transaction.oncomplete = resolve;
        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  get(entity) {
    return this.performTransaction(
      [this.entityStoreName],
      "readonly",
      (store, transaction, resolve, reject) => {
        const request = store.get(entity);
        request.onsuccess = () => resolve(request.result);
        request.onerror = (event) => reject(event.target.error);
      }
    );
  }

  getAll() {
    return this.performTransaction(
      [this.entityStoreName],
      "readonly",
      (store, transaction, resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = (event) => reject(event.target.error);
      }
    );
  }

  update(entity) {
    return this.performTransaction(
      [this.entityStoreName],
      "readwrite",
      (store, transaction, resolve, reject) => {
        transaction.oncomplete = resolve;
        transaction.onerror = (event) => reject(event.target.error);
        store.put(entity);
      }
    );
  }

  addThumbnail(thumbnail) {
    return this.performTransaction(
      [this.thumbnailStoreName],
      "readwrite",
      (store, transaction, resolve, reject) => {
        transaction.oncomplete = () => resolve(thumbnail);
        transaction.onerror = (event) => reject(event.target.error);
        store.put(thumbnail);
      }
    );
  }

  getThumbnail(entity, id) {
    return this.performTransaction(
      [this.thumbnailStoreName],
      "readonly",
      (store, transaction, resolve, reject) => {
        const request = store.get([entity, id]);
        request.onsuccess = () => resolve(request.result);
        request.onerror = (event) => reject(event.target.error);
      }
    );
  }

  getThumbnailsByEntity(entity) {
    return this.performTransaction(
      [this.thumbnailStoreName],
      "readonly",
      (store, transaction, resolve, reject) => {
        const index = store.index("Entity");
        const range = IDBKeyRange.only(entity);
        const request = index.getAll(range);

        request.onsuccess = () => resolve(request.result);
        request.onerror = (event) => reject(event.target.error);
      }
    );
  }

  getAllThumbnails() {
    return this.performTransaction(
      [this.thumbnailStoreName],
      "readonly",
      (store, transaction, resolve, reject) => {
        const index = store.index("Id");
        const request = index.getAll();

        request.onsuccess = () => {
          const fileIds = request.result.map(record => record.Id);
          resolve(fileIds);
        };
        request.onerror = (event) => reject(event.target.error);
      }
    );
  }

  deleteThumbnail(entity, id) {
    return this.performTransaction(
      [this.thumbnailStoreName],
      "readwrite",
      (store, transaction, resolve, reject) => {
        transaction.oncomplete = resolve;
        transaction.onerror = (event) => reject(event.target.error);
        store.delete([entity, id]);
      }
    );
  }
}
